import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { WorkspaceSelector } from './WorkspaceSelector';
import { Button, InlineStack, Text } from '@shopify/polaris';

export const AppHeader = ({ title = 'Cart Nexus Dashboard' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, refresh } = useAuth();
  const { currentWorkspace, isLoading, refreshCurrentWorkspace } = useWorkspace();
  const isAdminPage = location.pathname.startsWith('/admin');

  // Auto-refresh workspace from localStorage when component mounts
  React.useEffect(() => {
    if (!currentWorkspace && !isLoading) {
      refreshCurrentWorkspace();
    }
  }, [currentWorkspace, isLoading, refreshCurrentWorkspace]);

  // Extend token TTL when user switches workspace from the header
  // Skip this on admin pages as they don't require workspace context
  const prevWorkspaceId = React.useRef(currentWorkspace?.id);
  React.useEffect(() => {
    // Don't refresh on admin pages
    if (isAdminPage) return;
    
    // Only refresh if workspace actually changed (not just on mount)
    if (currentWorkspace?.id && currentWorkspace.id !== prevWorkspaceId.current) {
      prevWorkspaceId.current = currentWorkspace.id;
      try { refresh(); } catch (_) {}
    } else if (currentWorkspace?.id) {
      // Initialize the ref on first mount
      prevWorkspaceId.current = currentWorkspace.id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id, isAdminPage]);

  const handleLogout = async () => {
    await logout();
    localStorage.removeItem('selectedWorkspace');
    window.location.href = '/login';
  };

  // Always render a stable header; vary only the right-side content

  return (
    <div className="app-header">
      <InlineStack align="space-between" blockAlign="center">
        <div className="header-left">
          <Text variant="headingLg" fontWeight="bold" className="main-heading">
            {title}
          </Text>
        </div>
        
        <div className="header-right">
          <InlineStack gap="base" align="center">
            {isAdminPage ? (
              <div style={{ display: 'flex', gap: '12px' }}>
                <Button
                  variant="secondary"
                  onClick={() => navigate('/workspace')}
                >
                  Go to Main App
                </Button>
                <Button
                  variant="primary"
                  onClick={handleLogout}
                  className="logout-button"
                >
                  Logout
                </Button>
              </div>
            ) : (
              <>
                <Text variant="bodySm" color="subdued" className="workspace-label">
                  Workspace
                </Text>
                <WorkspaceSelector />
                <Button
                  variant="primary"
                  onClick={handleLogout}
                  className="logout-button"
                >
                  Logout
                </Button>
              </>
            )}
          </InlineStack>
        </div>
      </InlineStack>
    </div>
  );
};
